import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../components/Button';
import Card from '../components/Card';
import { useGameStore } from '../stores/gameStore';
import { getAvatarColor, getInitials } from '../utils/helpers';
import { gameService } from '../services/gameService';
import { useSocket } from '../context/SocketContext';

const Lobby = () => {
	const { t } = useTranslation();
	const contextGameId = useGameStore((state) => state.gameId);
	const players = useGameStore((state) => state.players);
	const host = useGameStore((state) => state.host);
	const playerId = useGameStore((state) => state.playerId);
	const error = useGameStore((state) => state.error);
	const resetGame = useGameStore((state) => state.resetGame);

	const { gameId } = useParams();
	const { socket, connected } = useSocket();
	const navigate = useNavigate();

	const hostId = host?.id;
	const hostName = host?.name || '';
	const isHost = playerId === hostId;

	useEffect(() => {
		if (!contextGameId || contextGameId !== gameId) {
			navigate('/');
		}
	}, [contextGameId, gameId, navigate]);

	const handleStart = () => {
		gameService.startGame(socket, connected, { playerId });
	};

	const handleExit = () => {
		gameService.exitGame(socket, connected, { playerId, gameId });
		resetGame();
	};

	return (
		<div className='container-game py-8'>
			<div className='text-center mb-8 animate-fade-in'>
				<h1 className='text-4xl font-bold text-white mb-2'>{t('lobbyScreen.title')}</h1>
				<p className='text-white font-bold text-xl mb-2'>
					{t('lobbyScreen.gameCodeDisplay')} {gameId}
				</p>
				<p className='text-white opacity-90'>
					{isHost ? t('lobbyScreen.shareCodeMessage') : t('lobbyScreen.waitingForHostMessage')}
				</p>
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				<div>
					<Card>
						<h2 className='text-xl font-bold mb-4'>
							{t('lobbyScreen.playersCountLabel', { count: players.length })}
						</h2>
						{players.length === 0 ? (
							<p className='text-gray-500'>{t('common.waitingForPlayersMessage')}</p>
						) : (
							<div className='space-y-2'>
								{players.map((player) => (
									<div key={player.playerId} className='player-card'>
										<div className={`avatar ${getAvatarColor(player.name)} mr-3`}>
											{getInitials(player.name)}
										</div>
										<span className='font-medium'>
											{player.name} {player.playerId === hostId ? t('common.hostSuffix') : ''}
										</span>
									</div>
								))}
							</div>
						)}
					</Card>
				</div>

				<div>
					<Card>
						<h2 className='text-xl font-bold mb-4'>{t('lobbyScreen.gameSettingsTitle')}</h2>
						<div className='space-y-2'>
							<p>
								<span className='font-medium'>{t('lobbyScreen.hostDisplay')}</span> {hostName}
							</p>
							<p>
								<span className='font-medium'>{t('lobbyScreen.playersNeededMessage')}</span> 2
							</p>
							<p>
								<span className='font-medium'>{t('lobbyScreen.statusLabel')}</span>{' '}
								{t('lobbyScreen.statusWaitingForPlayersValue')}
							</p>
						</div>

						{error && <div className='mt-4 p-3 bg-danger-100 text-danger-700 rounded-lg'>{error}</div>}

						<div className='mt-6 flex gap-3'>
							<Button variant='secondary' onClick={handleExit} className='flex-1'>
								{t('common.exitButton')}
							</Button>
							{isHost && (
								<Button
									variant='primary'
									onClick={handleStart}
									disabled={players.length < 2}
									className='flex-1'
									title={players.length < 2 ? t('lobbyScreen.disabledMinPlayersTooltip') : undefined}
								>
									{t('lobbyScreen.startGameButton')}
								</Button>
							)}
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default Lobby;
